import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';
import BreadcrumbNav, { BREADCRUMB_ITEMS } from '../components/BreadcrumbNav';
import { 
  ArrowRight, 
  Users, 
  Download, 
  Search, 
  Trash2, 
  Calendar,
  Mail,
  TrendingUp,
  Globe,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Coins,
  Home,
  Settings,
  LogOut
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Logo  
const LOGO_TRIANGLE = '/brand/ast-logo-asb606.png';

export default function CGMCoinDashboard() {
  const { user, token, logout } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  
  // View state: 'main' or 'subscribers'
  const [currentView, setCurrentView] = useState('main');
  
  const [subscribers, setSubscribers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/inbox/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Fetch subscribers
  const fetchSubscribers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('limit', 50);
      params.append('site_id', 'cgm_coin');
      if (searchQuery) params.append('q', searchQuery);
      if (fromDate) params.append('from', fromDate);
      if (toDate) params.append('to', toDate);

      const response = await axios.get(`${API_URL}/api/admin/inbox/subscribers?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setSubscribers(response.data.data);
        setTotal(response.data.total);
        setTotalPages(response.data.pages);
      }
    } catch (error) {
      console.error('Error fetching subscribers:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [token]);

  useEffect(() => {
    if (currentView === 'subscribers') {
      fetchSubscribers();
    }
  }, [currentView, currentPage, token]);

  // Search with debounce
  useEffect(() => {
    if (currentView === 'subscribers') {
      const timer = setTimeout(() => {
        setCurrentPage(1);
        fetchSubscribers();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [searchQuery, fromDate, toDate]);

  // Delete subscriber - FIXED
  const handleDelete = async (id) => {
    if (!window.confirm(language === 'ar' ? 'هل أنت متأكد من حذف هذا المشترك؟' : 'Are you sure you want to delete this subscriber?')) {
      return;
    }
    setDeleting(true);
    try {
      const response = await axios.delete(`${API_URL}/api/admin/inbox/subscribers/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Delete response:', response.data);
      await fetchSubscribers();
      await fetchStats();
    } catch (error) {
      console.error('Error deleting subscriber:', error);
      alert(language === 'ar' ? 'حدث خطأ أثناء الحذف' : 'Error deleting subscriber');
    } finally {
      setDeleting(false);
    }
  };

  // Bulk delete - FIXED using POST method
  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(language === 'ar' 
      ? `هل أنت متأكد من حذف ${selectedIds.length} مشترك؟` 
      : `Are you sure you want to delete ${selectedIds.length} subscribers?`)) {
      return;
    }
    setDeleting(true);
    try {
      // Use POST instead of DELETE for bulk operations
      const response = await axios.post(`${API_URL}/api/admin/inbox/subscribers/bulk-delete`, 
        { ids: selectedIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Bulk delete response:', response.data);
      setSelectedIds([]);
      await fetchSubscribers();
      await fetchStats();
    } catch (error) {
      console.error('Error bulk deleting:', error);
      alert(language === 'ar' ? 'حدث خطأ أثناء الحذف' : 'Error deleting subscribers');
    } finally {
      setDeleting(false);
    }
  };

  // Export CSV
  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      params.append('site_id', 'cgm_coin');
      if (searchQuery) params.append('q', searchQuery);
      if (fromDate) params.append('from', fromDate);
      if (toDate) params.append('to', toDate);

      const response = await axios.get(`${API_URL}/api/admin/inbox/export.csv?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cgm_coin_subscribers_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting:', error);
    } finally {
      setExporting(false);
    }
  };

  // Toggle select
  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // Select all
  const toggleSelectAll = () => {
    if (selectedIds.length === subscribers.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(subscribers.map(s => s.id));
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ar-AE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Main view with button
  if (currentView === 'main') {
    return (
      <div className="min-h-screen bg-[#030303]">
        {/* Background Effects */}
        <div className="fixed inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#d4af37]/5 rounded-full blur-[150px]" />
        </div>

        {/* Header */}
        <header className="sticky top-0 z-50 bg-[#050505]/80 backdrop-blur-2xl border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="h-16 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/dashboard')}
                  className="w-10 h-10 bg-white/[0.03] rounded-xl flex items-center justify-center hover:bg-[#d4af37]/10 transition-all border border-white/10 hover:border-[#d4af37]/40"
                >
                  <ArrowRight className="h-5 w-5 text-[#d4af37]" />
                </motion.button>
                
                <div className="flex items-center gap-3">
                  <motion.img 
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    src={LOGO_TRIANGLE}
                    alt="ASB606"
                    className="w-10 h-10 object-contain drop-shadow-[0_0_15px_rgba(212,175,55,0.4)]"
                  />
                  <div>
                    <h1 className="text-lg font-bold text-white flex items-center gap-2">
                      <Coins className="h-5 w-5 text-[#d4af37]" />
                      CGM COIN
                    </h1>
                    <p className="text-xs text-[#71717A]">
                      {language === 'ar' ? 'إدارة منصة العملة الرقمية' : 'Digital Currency Platform'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <a
                  href="https://digital-currency-43.preview.emergentagent.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-[#d4af37]/20 text-[#d4af37] rounded-xl hover:bg-[#d4af37]/30 transition-colors text-sm font-medium"
                >
                  <ExternalLink className="h-4 w-4" />
                  {language === 'ar' ? 'زيارة الموقع' : 'Visit Site'}
                </a>
                
                <LanguageToggle />
                <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => navigate('/dashboard')} className="w-10 h-10 bg-white/[0.03] rounded-xl flex items-center justify-center hover:bg-[#d4af37]/10 transition-all border border-white/10 hover:border-[#d4af37]/40">
                  <Home className="h-5 w-5 text-[#A1A1AA]" />
                </motion.button>
                <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => navigate('/account-settings')} className="w-10 h-10 bg-white/[0.03] rounded-xl flex items-center justify-center hover:bg-[#d4af37]/10 transition-all border border-white/10 hover:border-[#d4af37]/40">
                  <Settings className="h-5 w-5 text-[#A1A1AA]" />
                </motion.button>
                <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={logout} className="w-10 h-10 bg-red-500/10 rounded-xl flex items-center justify-center hover:bg-red-500/20 transition-all border border-red-500/20">
                  <LogOut className="h-5 w-5 text-red-400" />
                </motion.button>
                <div className="flex items-center gap-3 ps-3 border-s border-white/10">
                  <div className="text-end hidden sm:block">
                    <p className="text-sm font-medium text-[#F5F5F5]">{user?.name}</p>
                    <p className="text-xs text-[#71717A]">{language === 'ar' ? 'مدير' : 'Admin'}</p>
                  </div>
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#d4af37] to-[#c9a227] flex items-center justify-center text-black font-bold text-sm">
                    {user?.name?.charAt(0) || 'A'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <BreadcrumbNav items={BREADCRUMB_ITEMS.cgmCoin} />
        </div>

        {/* Main Content - Pre-registrants Button */}
        <main className="relative z-10 py-12">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-center">
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ y: -5 }}
                onClick={() => setCurrentView('subscribers')}
                className="group bg-white/[0.02] backdrop-blur-xl rounded-2xl p-8 border border-[#d4af37]/30 hover:border-[#d4af37] transition-all duration-300 cursor-pointer text-center max-w-md w-full"
                data-testid="btn-pre-registrants"
              >
                <div className="w-20 h-20 bg-[#d4af37]/10 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 border border-[#d4af37]/20">
                  <Users className="h-10 w-10 text-[#d4af37]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">
                  {language === 'ar' ? 'المسجلين مسبقاً' : 'Pre-registrants'}
                </h3>
                <p className="text-[#71717A] text-sm mb-4">
                  {language === 'ar' ? 'إدارة قائمة المشتركين المسجلين مسبقاً' : 'Manage pre-registered subscribers list'}
                </p>
                {stats && (
                  <div className="flex items-center justify-center gap-4 mt-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-[#d4af37]">{stats.total}</p>
                      <p className="text-xs text-[#71717A]">{language === 'ar' ? 'إجمالي' : 'Total'}</p>
                    </div>
                    <div className="w-px h-8 bg-white/10"></div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-emerald-400">{stats.today}</p>
                      <p className="text-xs text-[#71717A]">{language === 'ar' ? 'اليوم' : 'Today'}</p>
                    </div>
                  </div>
                )}
              </motion.button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Subscribers view
  return (
    <div className="min-h-screen bg-[#030303]">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#d4af37]/5 rounded-full blur-[150px]" />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#050505]/80 backdrop-blur-2xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-16 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setCurrentView('main')}
                className="w-10 h-10 bg-white/[0.03] rounded-xl flex items-center justify-center hover:bg-[#d4af37]/10 transition-all border border-white/10 hover:border-[#d4af37]/40"
                data-testid="back-btn"
              >
                <ArrowRight className="h-5 w-5 text-[#d4af37]" />
              </motion.button>
              
              <div className="flex items-center gap-3">
                <motion.img 
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  src={LOGO_TRIANGLE}
                  alt="ASB606"
                  className="w-10 h-10 object-contain drop-shadow-[0_0_15px_rgba(212,175,55,0.4)]"
                />
                <div>
                  <h1 className="text-lg font-bold text-white flex items-center gap-2">
                    <Users className="h-5 w-5 text-[#d4af37]" />
                    {language === 'ar' ? 'المسجلين مسبقاً' : 'Pre-registrants'}
                  </h1>
                  <p className="text-xs text-[#71717A]">CGM COIN</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Stats */}
              <div className="flex items-center gap-4 bg-white/[0.02] backdrop-blur-xl rounded-xl px-4 py-2 border border-white/10">
                <div className="text-center">
                  <p className="text-lg font-bold text-white">{stats?.total || 0}</p>
                  <p className="text-xs text-[#71717A]">{language === 'ar' ? 'إجمالي' : 'Total'}</p>
                </div>
                <div className="w-px h-8 bg-white/10"></div>
                <div className="text-center">
                  <p className="text-lg font-bold text-emerald-400">{stats?.today || 0}</p>
                  <p className="text-xs text-[#71717A]">{language === 'ar' ? 'اليوم' : 'Today'}</p>
                </div>
                <div className="w-px h-8 bg-white/10"></div>
                <div className="text-center">
                  <p className="text-lg font-bold text-blue-400">{stats?.this_week || 0}</p>
                  <p className="text-xs text-[#71717A]">{language === 'ar' ? 'الأسبوع' : 'Week'}</p>
                </div>
              </div>
              
              <LanguageToggle />
              <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => navigate('/dashboard')} className="w-10 h-10 bg-white/[0.03] rounded-xl flex items-center justify-center hover:bg-[#d4af37]/10 transition-all border border-white/10 hover:border-[#d4af37]/40">
                <Home className="h-5 w-5 text-[#A1A1AA]" />
              </motion.button>
              <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={logout} className="w-10 h-10 bg-red-500/10 rounded-xl flex items-center justify-center hover:bg-red-500/20 transition-all border border-red-500/20">
                <LogOut className="h-5 w-5 text-red-400" />
              </motion.button>
              <div className="flex items-center gap-3 ps-3 border-s border-white/10">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#d4af37] to-[#c9a227] flex items-center justify-center text-black font-bold text-sm">
                  {user?.name?.charAt(0) || 'A'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <BreadcrumbNav items={BREADCRUMB_ITEMS.cgmCoinSubscribers} />
      </div>

      {/* Content */}
      <main className="relative z-10 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden">
            {/* Filters */}
            <div className="p-4 border-b border-white/10">
              <div className="flex flex-col md:flex-row gap-4">
                {/* Search */}
                <div className="flex-1 relative">
                  <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#71717A]" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={language === 'ar' ? 'بحث بالبريد الإلكتروني...' : 'Search by email...'}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 pr-10 text-white placeholder-[#71717A] focus:outline-none focus:border-[#d4af37]"
                    data-testid="search-input"
                  />
                </div>
                
                {/* Date From */}
                <div className="flex items-center gap-2">
                  <span className="text-[#71717A] text-sm">{language === 'ar' ? 'من:' : 'From:'}</span>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white focus:outline-none focus:border-[#d4af37]"
                  />
                </div>
                
                {/* Date To */}
                <div className="flex items-center gap-2">
                  <span className="text-[#71717A] text-sm">{language === 'ar' ? 'إلى:' : 'To:'}</span>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white focus:outline-none focus:border-[#d4af37]"
                  />
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleExport}
                    disabled={exporting || subscribers.length === 0}
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-500/20 text-emerald-400 rounded-xl hover:bg-emerald-500/30 transition-colors disabled:opacity-50"
                    data-testid="export-btn"
                  >
                    {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                    <span>{language === 'ar' ? 'تصدير' : 'Export'}</span>
                  </button>
                  
                  {selectedIds.length > 0 && (
                    <button
                      onClick={handleBulkDelete}
                      disabled={deleting}
                      className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 rounded-xl hover:bg-red-500/30 transition-colors"
                      data-testid="bulk-delete-btn"
                    >
                      {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                      <span>{language === 'ar' ? `حذف (${selectedIds.length})` : `Delete (${selectedIds.length})`}</span>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/[0.03]">
                  <tr>
                    <th className="px-4 py-3 text-start">
                      <input
                        type="checkbox"
                        checked={selectedIds.length === subscribers.length && subscribers.length > 0}
                        onChange={toggleSelectAll}
                        className="rounded bg-white/5 border-white/10 w-4 h-4"
                        data-testid="select-all-checkbox"
                      />
                    </th>
                    <th className="px-4 py-3 text-start text-[#71717A] text-sm font-medium">#</th>
                    <th className="px-4 py-3 text-start text-[#71717A] text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        {language === 'ar' ? 'البريد الإلكتروني' : 'Email'}
                      </div>
                    </th>
                    <th className="px-4 py-3 text-start text-[#71717A] text-sm font-medium">
                      {language === 'ar' ? 'الاسم' : 'Name'}
                    </th>
                    <th className="px-4 py-3 text-start text-[#71717A] text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        {language === 'ar' ? 'تاريخ التسجيل' : 'Registered At'}
                      </div>
                    </th>
                    <th className="px-4 py-3 text-start text-[#71717A] text-sm font-medium">
                      {language === 'ar' ? 'إجراءات' : 'Actions'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="px-4 py-12 text-center">
                        <Loader2 className="h-8 w-8 animate-spin text-[#d4af37] mx-auto" />
                      </td>
                    </tr>
                  ) : subscribers.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-4 py-12 text-center text-[#71717A]">
                        {language === 'ar' ? 'لا يوجد مشتركين بعد' : 'No subscribers yet'}
                      </td>
                    </tr>
                  ) : (
                    subscribers.map((sub, idx) => (
                      <tr key={sub.id} className="border-t border-white/10 hover:bg-white/[0.02]">
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(sub.id)}
                            onChange={() => toggleSelect(sub.id)}
                            className="rounded bg-white/5 border-white/10 w-4 h-4"
                            data-testid={`checkbox-${sub.id}`}
                          />
                        </td>
                        <td className="px-4 py-3 text-[#71717A] text-sm">
                          {(currentPage - 1) * 50 + idx + 1}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-white font-mono text-sm">{sub.email}</span>
                        </td>
                        <td className="px-4 py-3 text-[#71717A]">
                          {sub.name || '-'}
                        </td>
                        <td className="px-4 py-3 text-[#71717A] text-sm">
                          {formatDate(sub.subscribed_at)}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleDelete(sub.id)}
                            disabled={deleting}
                            className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-colors disabled:opacity-50"
                            title={language === 'ar' ? 'حذف' : 'Delete'}
                            data-testid={`delete-btn-${sub.id}`}
                          >
                            {deleting ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="p-4 border-t border-white/10 flex items-center justify-between">
                <p className="text-[#71717A] text-sm">
                  {language === 'ar' 
                    ? `عرض ${(currentPage - 1) * 50 + 1} - ${Math.min(currentPage * 50, total)} من ${total}` 
                    : `Showing ${(currentPage - 1) * 50 + 1} - ${Math.min(currentPage * 50, total)} of ${total}`}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="p-2 bg-white/[0.03] rounded-lg disabled:opacity-50 hover:bg-white/[0.06] transition-colors border border-white/10"
                  >
                    <ChevronRight className="h-4 w-4 text-white" />
                    <ChevronRight className="h-4 w-4 text-white -mr-2" />
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="p-2 bg-white/[0.03] rounded-lg disabled:opacity-50 hover:bg-white/[0.06] transition-colors border border-white/10"
                  >
                    <ChevronRight className="h-4 w-4 text-white" />
                  </button>
                  <span className="text-white px-4">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="p-2 bg-white/[0.03] rounded-lg disabled:opacity-50 hover:bg-white/[0.06] transition-colors border border-white/10"
                  >
                    <ChevronLeft className="h-4 w-4 text-white" />
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="p-2 bg-white/[0.03] rounded-lg disabled:opacity-50 hover:bg-white/[0.06] transition-colors border border-white/10"
                  >
                    <ChevronLeft className="h-4 w-4 text-white" />
                    <ChevronLeft className="h-4 w-4 text-white -mr-2" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
